import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { brandText } from '../../../config/branding';
import { chartTimePeriod } from '../../../config/enum';
import currentTimeZone from '../../../config/timeZone';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import dashboard from '../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

/**
 * Component to display revenue vs power consumed chart
 * @returns
 */
export default function RevenuePowerConsumedChart() {
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: 'Revenue',
        data: powerConsumedData.map((d) => d.totalDebitAmount),
        borderColor: '#F79009',
        /**
         * Background
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Electricity',
        data: powerConsumedData.map((d) => d.totalPowerConsumed),
        borderColor: '#2E9E3A',
        /**
         * Function to create a gradient bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const chartContext = chart.ctx;
          const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
          gradient.addColorStop(0, 'rgba(46, 158, 58, 1)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0, // Remove points on the line
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === 'Revenue') {
              return 'rgba(247, 144, 9, 0.9)'; // Orange for Revenue
            }
            if (context.tooltipItems[0].dataset.label === 'Electricity') {
              return 'rgba(46, 158, 58, 0.9)'; // Green for Electricity
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param context
           * @returns
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === 'Revenue' ? 'Revenue' : 'Electricity';
          },
          /**
           * Function to show tooltip data
           * @param context
           * @returns
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === 'Revenue' ? brandText.currency : 'Kwh'}`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await dashboard.getPowerConsumedReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });
      if (range === 'last12months' && response.status === 200) {
        setPowerConsumedData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setPowerConsumedData(fillMissingLast24HrsGeneric(response.data));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last12months');
  }, []);

  return (
    <div className="revenue-power-consumer-chart-wrap">
      <div className="revenue-power-consumer-chart-header">
        <div className="header-title">
          <h6>
            <span className="power-consumer">Power Consumed</span>
            <span>Vs</span>
            <span className="revenue-growth">Revenue Growth</span>
          </h6>
          {/* <span className="header-arrow">{'->'}</span> */}
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => duration.label)}
          onToggle={(index, label) =>
            fetchPowerConsumedReport(
              chartTimePeriod.find((duration) => duration.label === label)?.value || 'last12months',
            )
          }
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      <div className="revenue-power-consumer-chart-container">
        <Line
          data={chartData}
          options={options}
          height={210} // Explicit height for the chart
          width={1000}
        />
      </div>
    </div>
  );
}
